export type Direction = "left" | "right" | "up" | "down";
export type StepResult = {
	moved: boolean;
	gained: number;
	won: boolean;
	lost: boolean;
};

export class GameEnv {
	readonly size: number;
	state: number[];
	score = 0;
	won = false;
	lost = false;

	constructor(size: number) {
		this.size = size;
		this.state = new Array(size * size).fill(0);
		this.reset();
	}

	reset(): number[] {
		this.state.fill(0);
		this.score = 0;
		this.won = false;
		this.lost = false;
		this.spawnTile();
		this.spawnTile();
		return this.state;
	}

	setState(state: number[], score: number, won: boolean, lost: boolean) {
		if (state.length !== this.size * this.size) {
			throw new Error(`State length ${state.length} does not match a ${this.size}x${this.size} board`);
		}
		this.state = [...state];
		this.score = score;
		this.won = won;
		this.lost = lost;
	}

	private spawnTile() {
		const empty: number[] = [];
		for (let i = 0; i < this.state.length; i++) {
			if (this.state[i] === 0) {
				empty.push(i);
			}
		}
		if (empty.length > 0) {
			this.state[empty[Math.floor(Math.random() * empty.length)]] = Math.random() < 0.9 ? 2 : 4;
		}
	}

	private lineIndices(line: number, direction: Direction): number[] {
		const n = this.size;
		const indices: number[] = [];
		for (let k = 0; k < n; k++) {
			switch (direction) {
				case "left":
					indices.push(line * n + k);
					break;
				case "right":
					indices.push(line * n + (n - 1 - k));
					break;
				case "up":
					indices.push(k * n + line);
					break;
				case "down":
					indices.push((n - 1 - k) * n + line);
					break;
			}
		}
		return indices;
	}

	private collapseLine(line: number[]): { line: number[]; moved: boolean; gained: number } {
		const tiles = line.filter(tile => tile !== 0);
		const merged: number[] = [];
		let gained = 0;
		for (let i = 0; i < tiles.length; i++) {
			if (i + 1 < tiles.length && tiles[i] === tiles[i + 1]) {
				const value = tiles[i] * 2;
				merged.push(value);
				gained += value;
				i++;
			} else {
				merged.push(tiles[i]);
			}
		}
		while (merged.length < line.length) {
			merged.push(0);
		}
		const moved = merged.some((value, i) => value !== line[i]);
		return { line: merged, moved, gained };
	}

	canMove(direction: Direction): boolean {
		if (this.won || this.lost) {
			return false;
		}
		for (let line = 0; line < this.size; line++) {
			const indices = this.lineIndices(line, direction);
			if (this.collapseLine(indices.map(i => this.state[i])).moved) {
				return true;
			}
		}
		return false;
	}

	legalMoves(): boolean[] {
		return [this.canMove("up"), this.canMove("down"), this.canMove("right"), this.canMove("left")];
	}

	movesAvailable(): boolean {
		if (this.state.includes(0)) {
			return true;
		}
		const n = this.size;
		for (let r = 0; r < n; r++) {
			for (let c = 0; c < n; c++) {
				const value = this.state[r * n + c];
				if (c + 1 < n && this.state[r * n + c + 1] === value) {
					return true;
				}
				if (r + 1 < n && this.state[(r + 1) * n + c] === value) {
					return true;
				}
			}
		}
		return false;
	}

	step(direction: Direction): StepResult {
		if (this.won || this.lost) {
			return { moved: false, gained: 0, won: this.won, lost: this.lost };
		}

		let moved = false;
		let gained = 0;
		for (let line = 0; line < this.size; line++) {
			const indices = this.lineIndices(line, direction);
			const result = this.collapseLine(indices.map(i => this.state[i]));
			if (result.moved) {
				moved = true;
				gained += result.gained;
				indices.forEach((stateIndex, k) => {
					this.state[stateIndex] = result.line[k];
				});
			}
		}

		if (!moved) {
			return { moved: false, gained: 0, won: false, lost: false };
		}

		this.score += gained;

		if (this.state.includes(2048)) {
			this.won = true;
			return { moved: true, gained, won: true, lost: false };
		}

		this.spawnTile();

		if (!this.movesAvailable()) {
			this.lost = true;
		}
		return { moved: true, gained, won: false, lost: this.lost };
	}
}
